import { defineStore } from 'pinia'
import { arrayContentEqualsIgnoreOrder } from '@/helpers/helpers.js'

interface State {
  localStorageKeys: Object,
  default: Object,
  saved: Object,
  current: Object,
  anyCurrentSettingDiffersFromDefault: Boolean,
  temporarySettingDiffersFromDefault: Object,
  possibleOptions: Object,
}

export const tripSettingsStore = defineStore('tripSettings', {
  state: (): State => ({
    localStorageKeys: {
      settings: 'Sachsenauskunft-Settings'
    },
    default: {
      standardSettings: {
        maxChanges: null, // gets initialized by possibleOptions
        mot: null, // gets initialized by possibleOptions
        walkingSpeed: null, // gets initialized by possibleOptions
        pathToStop: null, // gets initialized by possibleOptions
        // bikeOnStop: false,
        travelType: 'FOOT', // gets initialized by possibleOptions
        includeAlternativeStops: true
      },
      mobilitySettings: {
        mobilityRestriction: null, // gets initialized by possibleOptions
        entrance: null,
        escalators: true,
        solidStairs: true,
        leastChange: false
      },
      variousSettings: {
        wheelchair: false, // won't be submitted
        profile: null // won't be submitted
      }
    },
    /**
     * saved options and values from LocalStorage, that should be used for upcoming trip requests in later sessions.
     * vue store entries are only reactive when set initially, so this is a copy from default
     */
    saved: {
      standardSettings: {
        maxChanges: null,
        mot: null,
        walkingSpeed: null,
        pathToStop: null,
        // bikeOnStop: null,
        travelType: null,
        includeAlternativeStops: null
      },
      mobilitySettings: {
        mobilityRestriction: null,
        entrance: null,
        escalators: null,
        solidStairs: null,
        leastChange: null
      },
      variousSettings: {
        wheelchair: null,
        profile: null
      }
    },
    /**
     * current options and values used for next trip request (but not after new pageload).
     */
    current: {
      standardSettings: {},
      mobilitySettings: {},
      variousSettings: {}
    },
    /**
     * States whether values of these options differ from the default values or not.
     */
    anyCurrentSettingDiffersFromDefault: false,
    temporarySettingDiffersFromDefault: { // used for styling of badges in opened modal
      maxChanges: false, // standardSettings + accordion
      mot: false, // standardSettings + accordion
      walkingSpeed: false, // standardSettings
      pathToStop: false, // standardSettings
      // bikeOnStop: false, // standardSettings
      travelType: false, // standardSettings
      includeAlternativeStops: false, // standardSettings
      entrance: false, // mobilitySettings
      escalators: false, // mobilitySettings
      solidStairs: false, // mobilitySettings
      leastChange: false, // mobilitySettings
      wheelchair: false // variousSettings
    },
    /**
     * possible values of some options, requested from API
     */
    possibleOptions: {
      profile: null, // []
      entrance: null, // []
      mot: null, // []
      mobilityRestriction: null, // []
      maxChanges: null, // []
      walkingSpeed: null, // []
      travelType: null, // []
      pathToStop: null // []
    },
  }),
  actions: {
    // old mutations

    /**
     * Sets a single setting.
     * @function setSingleSetting
     * @param {String} settingsKey
     * @param {String} settingsValue
     */
    setSingleSetting ({ settingsKey, settingsValue }) {
      this[settingsKey] = settingsValue
    },
    /**
     * Sets a single setting of the given type.
     * @function setSingleSettingByType
     * @param {String} settingsKey
     * @param settingsValue
     * @param {String} settingsType - 'default', 'current' or 'saved'
     */
    setSingleSettingByType ({ settingsType, settingsKey, settingsValue }) {
      this[settingsType][settingsKey] = settingsValue
    },
    /**
     * Sets a single setting of the given type and group.
     * @function setSingleSettingByTypeAndGroup
     * @param {String} settingsKey
     * @param settingsValue
     * @param {String} settingsType - 'default', 'current' or 'saved'
     * @param {String} settingsGroup - 'standardSettings', 'mobilitySettings' or 'variousSettings'
     */
    setSingleSettingByTypeAndGroup ({ settingsType, settingsGroup, settingsKey, settingsValue }) {
      this[settingsType][settingsGroup][settingsKey] = settingsValue
    },

    /**
     * Sets the property temporarySettingDiffersFromDefault for one given entry.
     * @function setTemporarySettingDiffersFromDefault
     * @param {String} key
     * @param value
     */
    setTemporarySettingDiffersFromDefault ({ key, value }) {
      this.temporarySettingDiffersFromDefault[key] = value
    },

    /**
     * Sets the property anyCurrentSettingDiffersFromDefault to the given value.
     * @function setAnyCurrentSettingDiffersFromDefault
     * @param {Boolean} value
     */
    setAnyCurrentSettingDiffersFromDefault ({ value }) {
      this.anyCurrentSettingDiffersFromDefault = value
    },

    /**
     * Sets the property possibleOptions for one given entry.
     * @function setPossibleOptions
     * @param {String} key
     * @param value
     */
    setPossibleOptions ({ key, value }) {
      this.possibleOptions[key] = value
    },

    /**
     * Sets the property saved to the given value.
     * @function setSaved
     * @param {Object} value
     */
    setSaved ({ value }) {
      this.saved = value
    },

    /**
     * Sets the saved standardSettings to the given value.
     * @function setSavedStandardSettingsStore
     * @param {Object} value
     */
    setSavedStandardSettingsStore ({ value }) {
      this.saved.standardSettings = value
    },

    /**
     * Sets the saved mobilitySettings to the given value.
     * @function setSavedMobilitySettingsStore
     * @param {Object} value
     */
    setSavedMobilitySettingsStore ({ value }) {
      this.saved.mobilitySettings = value
    },

    /**
     * Sets the saved variousSettings to the given value.
     * @function setSavedVariousSettingsStore
     * @param {Object} value
     */
    setSavedVariousSettingsStore ({ value }) {
      this.saved.variousSettings = value
    },

    // old actions

    /**
     * Sets the given data as settings of the given type.
     * Every change of setting should go through this step
     * in order to check everything that needs to be checked.
     * So only this action uses commit with data that gets handed in.
     * @function modifySettings
     * @param {Object} settingsData - key value pairs
     * @param {String} settingsType - 'default', 'current' or 'saved'
     * @param {String} settingsGroup - 'standardSettings', 'mobilitySettings' or 'variousSettings'
     */
    modifySettings ({ settingsData, settingsType, settingsGroup }) {
      if (!settingsData) {
        console.error('Settings can not be modified: No data given.')
        return
      }

      if (!settingsType) {
        for (const [settingsKey, settingsValue] of Object.entries(settingsData)) {
          this.setSingleSetting({
            settingsKey,
            settingsValue
          })
        }

        if (settingsType === 'saved') {
          setLocalStorageSettings(this, this.saved)
        }

        return
      } else if (
        settingsType !== 'default' &&
        settingsType !== 'current' &&
        settingsType !== 'saved'
      ) {
        console.error('Settings can not be modified: Unknown type.')
        return
      }

      if (!settingsGroup) {
        for (const [settingsKey, settingsValue] of Object.entries(settingsData)) {
          this.setSingleSettingByType({
            settingsType,
            settingsKey,
            settingsValue
          })
        }

        if (settingsType === 'saved') {
          setLocalStorageSettings(this, this.saved)
        }

        return
      } else if (
        settingsGroup !== 'standardSettings' &&
        settingsGroup !== 'mobilitySettings' &&
        settingsGroup !== 'variousSettings'
      ) {
        console.error('Settings can not be modified: Unknown group.')
        return
      }

      for (const [settingsKey, settingsValue] of Object.entries(settingsData)) {
        this.setSingleSettingByTypeAndGroup({
          settingsType,
          settingsGroup,
          settingsKey,
          settingsValue
        })
      }

      if (settingsType === 'saved') {
        setLocalStorageSettings(this, this.saved)
      }

    },
    /**
     * Initializes the current settings with content of default settings and overrides single options with the ones saved and read from localStorage (if there are any).
     * This gets called every time the trip-planner page is reloaded.
     * @function initializeCurrentSettings
     */
    initializeCurrentSettings () {
      const savedSettings = getLocalStorageSettings(this)

      this.setCurrentSettingsToDefault()

      if (savedSettings) {
        this.modifySettings({
          settingsData: savedSettings,
          settingsType: 'saved'
        })

        this.applySavedSettingsToCurrentSettings()
      } else {
        // if no settings are available from localStorage, then we need to reset (=empty) saved in store!
        this.setSavedSettings({
          value: {
            standardSettings: {},
            mobilitySettings: {},
            variousSettings: {}
          }
        })
      }
    },

    /**
     * Sets possible values of some settings options and uses one value of each option to initialize the default settings.
     * @function initializePossibleSettingsOptions
     * @param {Object} optionsData
     */
    initializePossibleSettingsOptions ({ optionsData }) {
      if (!optionsData) {
        console.error('Options can not be set: No data provided.')
        return
      }

      // 1) options that come form API request
      for (const [apiKey, value] of Object.entries(optionsData)) {
        if (apiKey === 'numberOfChanges') { // trying to get rid of name inconsistency
          this.setPossibleOptions({
            key: 'maxChanges',
            value
          })
        } else if (apiKey === 'meansOfTransport') { // trying to get rid of name inconsistency
          this.setPossibleOptions({
            key: 'mot',
            value
          })
        } else if (apiKey === 'mobilityRestrictions') { // trying to get rid of name inconsistency
          this.setPossibleOptions({
            key: 'mobilityRestriction',
            value
          })
        } else {
          this.setPossibleOptions({
            key: apiKey,
            value
          })
        }
      }

      // 2) options that we want to provide
      const profileOptions = ['WHEELCHAIR', 'STROLLER', 'BIKE', 'SENIOR']
      const pathToStopOptions = ['5', '10', '15', '20', '25', '30']
      const travelTypeOptions = ['FOOT', 'BIKE', 'BIKE_AND_RIDE']

      this.setPossibleOptions({
        key: 'profile',
        value: profileOptions
      })

      this.setPossibleOptions({
        key: 'pathToStop',
        value: pathToStopOptions
      })

      this.setPossibleOptions({
        key: 'travelType',
        value: travelTypeOptions
      })

      // modify default standardSettings by overriding some settings
      this.modifySettings({
        settingsData: {
          mot: optionsData.meansOfTransport,
          maxChanges: optionsData.numberOfChanges[0], // first value
          walkingSpeed: optionsData.walkingSpeed[Math.floor(optionsData.walkingSpeed.length / 2)], // middle value
          pathToStop: pathToStopOptions[0], // first value
          travelType: travelTypeOptions[0] // first value
        },
        settingsType: 'default',
        settingsGroup: 'standardSettings'
      })

      // modify default mobilitySettings by overriding some settings
      this.modifySettings({
        settingsData: {
          entrance: optionsData.entrance[0],
          mobilityRestriction: optionsData.mobilityRestrictions[0]
        },
        settingsType: 'default',
        settingsGroup: 'mobilitySettings'
      })
    },

    /**
     * Copys all settings from the default settings to the current settings.
     * @function setCurrentSettingsToDefault
     */
    setCurrentSettingsToDefault () {
      // Attention: create shallow copy, not deep!

      this.modifySettings({
        settingsData: Object.assign({}, this.default.standardSettings),
        settingsType: 'current',
        settingsGroup: 'standardSettings'
      })

      this.modifySettings({
        settingsData: Object.assign({}, this.default.mobilitySettings),
        settingsType: 'current',
        settingsGroup: 'mobilitySettings'
      })

      this.modifySettings({
        settingsData: Object.assign({}, this.default.variousSettings),
        settingsType: 'current',
        settingsGroup: 'variousSettings'
      })
    },

    /**
     * Modifies the current settings with all saved settings.
     * @function applySavedSettingsToCurrentSettings
     */
    applySavedSettingsToCurrentSettings () {
      // Attention: create shallow copy, not deep!

      this.modifySettings({
        settingsData: Object.assign({}, this.saved.standardSettings),
        settingsType: 'current',
        settingsGroup: 'standardSettings'
      })

      this.modifySettings({
        settingsData: Object.assign({}, this.saved.mobilitySettings),
        settingsType: 'current',
        settingsGroup: 'mobilitySettings'
      })

      this.modifySettings({
        settingsData: Object.assign({}, this.saved.variousSettings),
        settingsType: 'current',
        settingsGroup: 'variousSettings'
      })
    },

    /**
     * Copys all current settings that differ from default settings and sets the saved settings with this data.
     * Clears all previously saved settings.
     * @function saveCurrentSettingsThatDifferFromDefault
     */
    saveCurrentSettingsThatDifferFromDefault () {
      this.setSavedSettings({
        value: {
          standardSettings: getSettingsThatDifferFromDefault(this, 'current', 'standardSettings'),
          mobilitySettings: getSettingsThatDifferFromDefault(this, 'current', 'mobilitySettings'),
          variousSettings: getSettingsThatDifferFromDefault(this, 'current', 'variousSettings')
        }
      })
    },

    /**
     * Sets saved settings to the given value.
     * Updates local storage.
     * @function setSavedSettings
     */
    setSavedSettings ({ value }) {
      this.setSaved({
        value
      })

      setLocalStorageSettings(this, this.saved)
    },

    /**
     * Sets saved standardSettings to the given value.
     * Updates local storage.
     * @function setSavedStandardSettings
     */
    setSavedStandardSettings ({ value }) {
      this.setSavedStandardSettingsStore({
        value
      })

      setLocalStorageSettings(this, this.saved)
    },

    /**
     * Sets saved mobilitySettings to the given value.
     * Updates local storage.
     * @function setSavedMobilitySettings
     */
    setSavedMobilitySettings ({ value }) {
      this.setSavedMobilitySettingsStore({
        value
      })

      setLocalStorageSettings(this, this.saved)
    },

    /**
     * Sets saved variousSettings to the given value.
     * Updates local storage.
     * @function setSavedVariousSettings
     */
    setSavedVariousSettings ({ value }) {
      this.setSavedVariousSettingsStore({
        value
      })

      setLocalStorageSettings(this, this.saved)
    },

    /**
     * Copys all settings from the current settings to the saved settings.
     * @function setSavedSettingsToCurrentSettings
     */
    setSavedSettingsToCurrentSettings () {
      // Attention: create shallow copy, not deep!
      this.modifySettings({
        settingsData: Object.assign({}, this.current.standardSettings),
        settingsType: 'saved',
        settingsGroup: 'standardSettings'
      })

      this.modifySettings({
        settingsData: Object.assign({}, this.current.mobilitySettings),
        settingsType: 'saved',
        settingsGroup: 'mobilitySettings'
      })

      this.modifySettings({
        settingsData: Object.assign({}, this.current.variousSettings),
        settingsType: 'saved',
        settingsGroup: 'variousSettings'
      })
    },

    /**
     * Deletes all saved settings.
     * @function resetSavedSearchSettings
     */
    resetSavedSearchSettings () {
      this.setSavedSettings({
        value: {
          standardSettings: {},
          mobilitySettings: {},
          variousSettings: {}
        }
      })
    },

    /**
     * Checks if any of the current settings is different from the default settings.
     * @function checkIfAnyCurrentSettingDiffersFromDefault
     */
    checkIfAnyCurrentSettingDiffersFromDefault () {
      let differs = 0

      for (const [settingName, settingValue] of Object.entries(this.current.standardSettings)) {
        switch (settingName) {
          case 'mot':
            differs = (!arrayContentEqualsIgnoreOrder(this.default.standardSettings[settingName], settingValue)) ? ++differs : differs
            break
          default:
            differs = (this.default.standardSettings[settingName] !== settingValue) ? ++differs : differs
            break
        }
      }

      for (const [settingName, settingValue] of Object.entries(this.current.mobilitySettings)) {
        differs = (this.default.mobilitySettings[settingName] !== settingValue) ? ++differs : differs
      }

      for (const [settingName, settingValue] of Object.entries(this.current.variousSettings)) {
        differs = (this.default.variousSettings[settingName] !== settingValue) ? ++differs : differs
      }

      // turn number into boolean
      differs = !!differs

      this.setAnyCurrentSettingDiffersFromDefault({
        value: differs
      })
    },

    /**
     * Checks if the given value of a setting with the given name is different from the default of this setting.
     * @function checkIfTemporarySettingDiffersFromDefault
     * @param {Object} state
     * @param {String} settingName
     * @param {} settingValue
     */
    checkIfTemporarySettingDiffersFromDefault ({ settingName, settingValue }) {
      let differs

      switch (settingName) {
        case 'mot':
          differs = !arrayContentEqualsIgnoreOrder(this.default.standardSettings[settingName], settingValue)
          break
        case 'maxChanges':
        case 'walkingSpeed':
        case 'pathToStop': // case 'bikeOnStop':
        case 'travelType':
        case 'includeAlternativeStops':
          differs = this.default.standardSettings[settingName] !== settingValue
          break
        case 'mobilityRestriction':
        case 'entrance':
        case 'escalators':
        case 'solidStairs':
        case 'leastChange':
          differs = this.default.mobilitySettings[settingName] !== settingValue
          break
        case 'wheelchair':
        case 'profile':
          differs = this.default.variousSettings[settingName] !== settingValue
          break
        default:
          console.error('The setting can not be checked: Unknown setting.')
          return
      }

      this.setTemporarySettingDiffersFromDefault({
        key: settingName,
        value: differs
      })
    },

    /**
     * Removes a single mot with the given name from the saved mot.
     * @function removeSingleSavedMot
     * @param {Object} state
     * @param {String} mot - the mot to remove
     */
    removeSingleSavedMot ({ mot }) {
      mot = [...this.saved.standardSettings.mot].filter((name) => name !== mot)

      // do not allow to save 0 mot, beause it is senseless
      if (!mot.length) {
        // set mot back to default, by removing property mot from saved
        const { mot, ...newStandardSettings } = { ...this.saved.standardSettings }

        this.setSavedStandardSettings({
          value: newStandardSettings
        })
      } else {
        this.modifySettings({
          settingsData: {
            mot
          },
          settingsType: 'saved',
          settingsGroup: 'standardSettings'
        })
      }
    },

    /**
     * Removes all mot from the saved mot.
     * Do not allow to save 0 mot, beause it is senseless, so remove property mot from saved.
     * @function removeAllSavedMot
     */
    removeAllSavedMot () {
      const { mot, ...newStandardSettings } = { ...this.saved.standardSettings } // remove property mot from saved

      this.setSavedStandardSettings({
        value: newStandardSettings
      })
    },

    /**
     * Removes a single saved opion with the given name from the saved options.
     * @function removeSingleSavedOption
     * @param {String} option - the option to remove
     */
    removeSingleSavedOption ({ option, settingsType }) {
      // remove property (that is named like the string in the variable 'option') from saved
      const oldSettings = { ...this.saved[settingsType] }
      const newSettings = Object.keys(oldSettings)
        .filter((key) => key !== option)
        .reduce((cur, key) => {
          return Object.assign(cur, { [key]: oldSettings[key] })
        }, {})

      if (settingsType === 'standardSettings') {
        this.setSavedStandardSettings({
          value: newSettings
        })
      } else if (settingsType === 'mobilitySettings') {
        this.setSavedMobilitySettings({
          value: newSettings
        })
      } else if (settingsType === 'variousSettings') {
        this.setSavedVariousSettings({
          value: newSettings
        })
      }
    },

    /**
     * Removes all options from the saved options.
     * Mot does not belong to the options, see user-settings page.
     * @function removeAllSavedOptions
     */
    removeAllSavedOptions () {
      const { mot } = { ...this.saved.standardSettings } // remove all properties except mot

      this.setSavedSettings({
        value: {
          standardSettings: { mot }, // keep mot if existing
          mobilitySettings: {}, // delete all
          variousSettings: {} // delete all
        }
      })
    },

    /**
     * Update the setting mobilityRestriction according to some other settings.
     * @function updateMobilityRestriction
     */
    updateMobilityRestriction ({ wheelchair, solidStairs, escalators, leastChange, entrance }) {
      let mobilityRestriction = 'NONE'

      if (wheelchair) {
        mobilityRestriction = 'HIGH'
      }

      if (!solidStairs || !escalators || leastChange || entrance !== 'ANY') {
        mobilityRestriction = 'INDIVIDUAL'
      }

      this.modifySettings({
        settingsData: {
          mobilityRestriction
        },
        settingsType: 'current',
        settingsGroup: 'mobilitySettings'
      })
    },

    /**
     * Reset the given profile.
     * @function removeSavedProfile
     */
    removeSavedProfile () {
      const { profile, ...newSettings } = { ...this.saved.variousSettings }

      this.setSavedVariousSettings({
        value: newSettings
      })
    },

    /**
     * Applies settings parsed from a shared URL to current settings.
     * Overrides current settings without touching saved settings or localStorage.
     * @function applySharedSettings
     * @param {Object} settings - raw URL param values (strings/arrays)
     */
    applySharedSettings ({ settings }: { settings: Record<string, any> }) {
      const { mot, travelType, walkingSpeed, pathToStop, maxChanges, solidStairs, includeAlternativeStops } = settings

      if (mot !== undefined) {
        this.modifySettings({
          settingsData: { mot: Array.isArray(mot) ? mot : [mot] },
          settingsType: 'current',
          settingsGroup: 'standardSettings'
        })
      }

      const standardData: Record<string, any> = {}
      if (travelType !== undefined) standardData.travelType = travelType
      if (walkingSpeed !== undefined) standardData.walkingSpeed = walkingSpeed
      if (pathToStop !== undefined) standardData.pathToStop = pathToStop
      if (maxChanges !== undefined) standardData.maxChanges = maxChanges
      if (includeAlternativeStops !== undefined) {
        standardData.includeAlternativeStops = includeAlternativeStops === 'true' || includeAlternativeStops === true
      }

      if (Object.keys(standardData).length) {
        this.modifySettings({
          settingsData: standardData,
          settingsType: 'current',
          settingsGroup: 'standardSettings'
        })
      }

      if (solidStairs !== undefined) {
        this.modifySettings({
          settingsData: { solidStairs: solidStairs === 'true' || solidStairs === true },
          settingsType: 'current',
          settingsGroup: 'mobilitySettings'
        })
      }

      this.checkIfAnyCurrentSettingDiffersFromDefault()
    }
  },
  getters: {
    savedSettings (state) {
      return {
        ...state.saved.standardSettings,
        ...state.saved.mobilitySettings,
        ...state.saved.variousSettings
      }
    },

    /**
     * Checks if any of the standard, mobility or various settings are saved.
     * @function anySettingsAreSaved
     * @param {Object} state
     * @returns {Boolean}
     */
    anySettingsAreSaved (state) {
      if (
        Object.entries(state.saved.standardSettings).length ||
        Object.entries(state.saved.mobilitySettings).length ||
        Object.entries(state.saved.variousSettings).length
      ) {
        return true
      } else {
        return false
      }
    },

    /**
     * Checks if any of the standard, mobility or various settings differ from the default settings.
     * @function anyTemporarySettingDiffersFromDefault
     * @param {Object} state
     * @returns {Boolean}
     */
    anyTemporarySettingDiffersFromDefault (state) {
      for (const value of Object.values(state.temporarySettingDiffersFromDefault)) {
        if (value) {
          return true
        }
      }

      return false
    },

    /**
     * Checks if any of the mobility settings differ from the default settings.
     * @function anyMobilityRestrictionDiffersFromDefault
     * @param {Object} state
     * @returns {Boolean}
     */
    anyMobilityRestrictionDiffersFromDefault (state) {
      return (
        state.temporarySettingDiffersFromDefault.wheelchair ||
        state.temporarySettingDiffersFromDefault.escalators ||
        state.temporarySettingDiffersFromDefault.solidStairs ||
        state.temporarySettingDiffersFromDefault.leastChange ||
        state.temporarySettingDiffersFromDefault.entrance
      )
    },

    /**
     * Checks if any of the way to stop settings differ from the default settings.
     * @function anyWayToStopSettingDiffersFromDefault
     * @param {Object} state
     * @returns {Boolean}
     */
    anyWayToStopSettingDiffersFromDefault (state) {
      return (
        state.temporarySettingDiffersFromDefault.travelType ||
        // state.temporarySettingDiffersFromDefault.bikeOnStop ||
        state.temporarySettingDiffersFromDefault.walkingSpeed ||
        state.temporarySettingDiffersFromDefault.pathToStop
      )
    },
    currentStandardSettings (state) {
      return state.current.standardSettings
    },
    currentMobilitySettings (state) {
      return state.current.mobilitySettings
    },
    currentVariousSettings (state) {
      return state.current.variousSettings
    },
    defaultStandardSettings (state) {
      return state.default.standardSettings
    },
    defaultMobilitySettings (state) {
      return state.default.mobilitySettings
    },
    defaultVariousSettings (state) {
      return state.default.variousSettings
    },
    savedStandardSettings (state) {
      return state.saved.standardSettings
    },
    savedMobilitySettings (state) {
      return state.saved.mobilitySettings
    },
    savedVariousSettings (state) {
      return state.saved.variousSettings
    }
  }
})



/**
 * Gets settings data stored in localStorage.
 * @function getLocalStorageSettings
 * @param {Object} state
 * @returns {Object} settings
 */
function getLocalStorageSettings (state) {
  const storageItem = localStorage.getItem(state.localStorageKeys.settings)
  let storageObject = {}
  let settings = null

  if (!storageItem) {
    return settings
  }

  if (storageItem === 'undefined') { // undefined as a String
    localStorage.removeItem(state.localStorageKeys.settings)
  } else {
    storageObject = JSON.parse(storageItem)

    if (storageObject) {
      settings = storageObject
    } // else { handle migration from older versions }
  }

  return settings
}

/**
 * Sets localStorage settings data to the given settingsData or removes it in case there are no settings.
 * @function setLocalStorageSettings
 * @param {Object} state
 * @param {Object} settings
 */
function setLocalStorageSettings (state, settingsData) {
  const standardSettingsCount = Object.keys(settingsData?.standardSettings).length
  const mobilitySettingsCount = Object.keys(settingsData?.mobilitySettings).length
  const variousSettingsCount = Object.keys(settingsData?.variousSettings).length

  if (standardSettingsCount || mobilitySettingsCount || variousSettingsCount) {
    localStorage.setItem(state.localStorageKeys.settings, JSON.stringify(settingsData))
  } else {
    // remove when there are no settings
    localStorage.removeItem(state.localStorageKeys.settings)
  }
}

/**
 * Get all entries of a special settings type and group, that differ from type 'default' settings in the corresponding group.
 * Does not save mobilityRestriction because its only calculated from other settings.
 * @function getSettingsThatDifferFromDefault
 * @returns settings
 */
function getSettingsThatDifferFromDefault (state, settingsType, settingsGroup) {
  const settings = {}
  const settingsTypeToCompareWith = 'default'

  for (const [key, value] of Object.entries(state[settingsType][settingsGroup])) {
    if (state[settingsType][settingsGroup][key] !== state[settingsTypeToCompareWith][settingsGroup][key]) {
      if (key !== 'mobilityRestriction') { // do not save mobilityRestriction because its only calculated from other settings
        settings[key] = value
      }
    }
  }

  return settings
}
